/**
 * SOLID Review
 * S — Strong. One concern: hold the table — the game store's data, written only through `ClientState`,
 *     and the one action `ClientState.apply` asks for first when a decision resets the table.
 * O — Good. A new field is one member of its concern's state interface and one value in `emptyGameData`;
 *     nothing here changes.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Data and `resetAll`.
 * D — Strong. zustand, and the Application's store type and starting data.
 */
import { create } from 'zustand';
import { emptyGameData } from '../../application/state/emptyClientState';
import type { GameStore } from '../../application/state/gameStoreTypes';

/**
 * THE GAME STORE, INSIDE INFRASTRUCTURE (frontend strict-ports plan, FE6c, 2026-10-04). It was the
 * Framework's for the app's whole life — the "God store" of ~25 fields and ~35 actions that React wrote
 * from 35 files, split into seven slices in 2026-07. FE1–FE5 moved every write behind `ClientState`,
 * which applies a decision's changes; FE6c moves the store to the box that owns the client state and
 * deletes what that left behind:
 *
 *   - the slices, whose actions had all gone — what remained was starting values, now the Application's
 *     `emptyGameData`, shared with the Framework's null view;
 *   - the last two actions, `setLocalUser` and `selectCurrentPhase`, read only by tests (§29.4b);
 *   - the FE1 middleware that recomputed `derived` after every write while the FE2–FE5 flows still wrote
 *     the store directly: every write is a decision's now, and a decision that changes the table saves
 *     its derived values with it.
 *
 * React reads it through `ClientStateView` only; the composition root hands it to Infrastructure as a
 * host resource (`IServiceTableStore`), so a test core runs over this same store.
 */

/** Frozen at module load, as the slices' `Date.now()` was: `resetAll` restores this same stamp. */
const STARTED_AT = Date.now();

export const useGameStore = create<GameStore>()((set) => ({
  ...emptyGameData(STARTED_AT),

  resetAll() {
    set(emptyGameData(STARTED_AT));
  },
}));
